import {
  BrowserRouter,
  Route,
  Switch,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import Example from "../presentation/page/Example";

import PrivateRoute from "./PrivateRoute";

// Page
import Profil from "../presentation/page/Profil";
import Ui from "../presentation/page/Ui";
import Form from "../presentation/page/Form";
import Folder from "../presentation/page/Folder";
import ManageFolder from "../presentation/page/ManageFolder";
import DocusignCallback from "../presentation/page/DocusignCallback";
import Dashboard from "../presentation/page/Dashboard";
import CreateFolderClient from "../presentation/page/CreateFolderClient";

import envVariable from "../presentation/util/envVariable";
import Authentication from "../oauth/Authentication";
import DashboardDossier from "../presentation/page/DashboardDossier";
import { useQuery } from "../infrastructure/hooks";
import AccessFolder from "../presentation/page/AccessFolder";
import storage from "../presentation/util/storage";
import CreateUseCase from "../useCase/Folder/CreateUseCase";
import FetchFolderGateway from "../gateway/Folder/FetchFolderGateway";
import PlateformeFolder from "../presentation/page/PlateformeFolder";
import { EModule } from "../domain/User/Emodule";
import PreSignatureValidationFolder from "../presentation/page/PreSignatureValidationFolder";

const Authorization = (): JSX.Element => {
  const location = useLocation();
  const code = new URLSearchParams(location.search).get("code");

  Authentication.login(code).then(async function success(greatAccess) {
    if (greatAccess) {
      await Authentication.refreshProfile();
      window.location.pathname = envVariable("REACT_APP_REDIRECT_URL");
    }
  });
  return <></>;
};

const Loggout = (): JSX.Element => {
  Authentication.logout().then(function success() {
    window.location.replace(`${envVariable("REACT_APP_OAUTH_LOGOUT_URL")}`);
  });
  return <></>;
};

const CheckSSO = (): JSX.Element => {
  const query = useQuery();
  const extranetToken = query.get("extranet") || null;
  const folderId = query.get("folder_id") || null;
  const fromClient = query.get("from_client_id") || null;
  const fromClientId = fromClient ? parseInt(fromClient) : null;
  const requestContext = query.get("request_context") || null;
  const accountId = query.get("account_id") || null;
  const history = useHistory();

  // Compte/rôle sélectionné dans l'extranet : mémorisé "en attente" puis appliqué
  // sur l'état multi-compte une fois le profil chargé.
  storage.setPendingSsoContext(requestContext, accountId);

  Authentication.checkConnectedUser(extranetToken).then((reconnect) => {
    if (reconnect) {
      Authentication.logout().then(() => {
        // logout() purge le contexte : on le restaure pour la nouvelle session SSO.
        storage.setPendingSsoContext(requestContext, accountId);
        if (!Authentication.authorize(true, extranetToken)) {
          window.location.replace(`/logout`);
        }
      });
    } else {
      Authentication.isLogged().then((isAuthenticated) => {
        if (!isAuthenticated) {
          if (!Authentication.authorize(true, extranetToken)) {
            window.location.replace(`/logout`);
          }
          // Redirection vers l'OAuth en cours : la sélection SSO sera appliquée
          // après login via refreshProfile(). Ne pas la consommer ici.
          return;
        }

        // Déjà authentifié (checkConnectedUser n'a pas rafraîchi le profil) :
        // appliquer la sélection SSO sur le compte/rôle actif.
        storage.applyPendingSsoContext();

        if (folderId) {
          window.location.pathname = `/dossier/${folderId}`;
        } else if (fromClientId && !isNaN(fromClientId)) {
          const createUseCase = new CreateUseCase(new FetchFolderGateway());
          createUseCase
            .execute(storage.getClientFormId(), fromClientId)
            .then((newFolderId) => {
              // Navigate to new URL structure with folderId
              history.push(`/dossier/${newFolderId}/form/`);
            });
        } else {
          window.location.pathname = envVariable("REACT_APP_REDIRECT_URL");
        }
      });
    }
  });

  return <></>;
};

const Router = (): JSX.Element => {
  return (
    <BrowserRouter>
      <Switch>
        <Route path="/ui" component={Ui} />
        <Route path="/authorization" component={Authorization} />
        <Route path="/logout" component={Loggout} />
        <Route path="/sso-check" component={CheckSSO} />
        <Route
          path="/docusign/success/:dossierId?"
          component={DocusignCallback}
        />
        <PrivateRoute path="/dossier/:dossierId" component={AccessFolder} exact={true} />
        <PrivateRoute
          path="/dossier/:dossierId/form/:pageId?/:blockId?"
          component={Form}
        />
        <PrivateRoute path="/form/:pageId?/:blockId?" component={Form} />
        <PrivateRoute
          path="/dossiers/:state?/:currentPage?"
          component={Folder}
        />
        <PrivateRoute
          path="/manage-dossier/statut-integration/:state?/:currentPage?"
          component={ManageFolder}
          roles={["manage_dossier"]}
        />
        <PrivateRoute
          path="/manage-dossier/:dossierId"
          component={DashboardDossier}
          roles={["manage_dossier"]}
        />
        <PrivateRoute
          path="/plateforme-dossier/statut-plateforme/:state?/:currentPage?"
          component={PlateformeFolder}
          roles={['manager', 'partner']}
          modules={["platform_validator"]}
        />
        <PrivateRoute
          path="/plateforme-dossier/:dossierId"
          component={DashboardDossier}
          roles={['manager', 'partner']}
          modules={["platform_validator"]}
        />
        <PrivateRoute
          path="/pre-signature-validation/:dossierId"
          component={DashboardDossier}
          roles={["back_office_user"]}
          modules={[EModule.PreSignatureValidator]}
        />
        <PrivateRoute
          path="/pre-signature-validation"
          component={PreSignatureValidationFolder}
          roles={["back_office_user"]}
          modules={[EModule.PreSignatureValidator]}
        />
        <PrivateRoute
          path="/tableau-de-bord/:currentTab"
          component={Dashboard}
        />
        <PrivateRoute path="/tableau-de-bord/" component={Dashboard} />
        <PrivateRoute
          path="/creer-un-dossier-client/"
          component={CreateFolderClient}
        />
        <PrivateRoute exact path="/profile">
          <Profil />
        </PrivateRoute>
        <Route path="/example" component={Example} />
        {/* Not Found */}
        <PrivateRoute component={() => <Redirect to="/dossiers" />} />
      </Switch>
    </BrowserRouter>
  );
};

export default Router;
